import { client } from './api';

/**
 * The fax inbox.
 *
 * A transmission is not a document: twenty pages can arrive as twenty of these
 * and one of these can hold three documents that were stapled together, so the
 * inbox shows what arrived and the PDF manager turns it into something usable.
 *
 * Every call passes `throwOnError` because the generated client resolves rather
 * than rejects on a 4xx by default, which would show a success toast over a 403.
 */

export interface ReceivedFaxDocument {
  id: string;
  status: string;
  content_type: string | null;
  file_size: number | null;
}

export interface ReceivedFax {
  id: string;
  status: string;
  received_at: string;
  fax_number_from: string | null;
  fax_number_to: string | null;
  subject: string | null;
  page_count: number | null;
  agency_clinic_id: string | null;
  /** Set once every arrival on this fax has been built into a document. */
  filed_at?: string | null;
}

export interface ReceivedFaxDetail extends ReceivedFax {
  documents: ReceivedFaxDocument[];
}

interface DocumentDownload {
  url: string;
}

export interface ReceivedFaxPage {
  items: ReceivedFax[];
  total: number;
}

export interface ListReceivedFaxesParams {
  status?: string;
  /**
   * Only faxes from this sender. Compared digit to digit on the server, so
   * punctuation and the country code do not have to match and a partial number
   * narrows the list. Filtered there rather than here because the list is
   * paged: filtering the loaded page would hide the arrivals being looked for.
   */
  faxNumberFrom?: string;
  /**
   * Also return faxes every arrival of which has been built into a document.
   * Left out by default: the inbox is a working list, not an archive.
   */
  includeFiled?: boolean;
  limit?: number;
  offset?: number;
}

export async function listReceivedFaxes(
  params: ListReceivedFaxesParams = {}
): Promise<ReceivedFaxPage> {
  const response = await client.get<ReceivedFaxPage, unknown, true>({
    query: {
      ...(params.status ? { status: params.status } : {}),
      ...(params.faxNumberFrom
        ? { fax_number_from: params.faxNumberFrom }
        : {}),
      ...(params.includeFiled ? { include_filed: true } : {}),
      limit: params.limit ?? 25,
      offset: params.offset ?? 0,
    },
    responseType: 'json',
    throwOnError: true,
    url: '/received-faxes',
  });
  return response.data;
}

export async function getReceivedFax(
  faxId: string
): Promise<ReceivedFaxDetail> {
  const response = await client.get<ReceivedFaxDetail, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/received-faxes/${faxId}`,
  });
  return response.data;
}

/**
 * A short-lived signed URL for one document's contents.
 *
 * Opened rather than fetched: the object is served as an attachment from the
 * bucket's own origin, which is what keeps an HTML cover page from an unknown
 * sender out of our page.
 */
export async function getReceivedFaxDocumentUrl(
  faxId: string,
  documentId: string
): Promise<string> {
  const response = await client.get<DocumentDownload, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/received-faxes/${faxId}/documents/${documentId}/download`,
  });
  return response.data.url;
}
